import React from 'react'
import { formatWon } from '../../lib/hiddenInsuranceMoneyUtils'

const COLOR_ESTIMATED = '#7dd3fc'
const COLOR_ESTIMATED_STROKE = '#0284c7'
const COLOR_RECEIVED = '#1e3a5f'
const COLOR_GAP = '#fde68a'
const COLOR_GAP_STROKE = '#d97706'
const COLOR_TRACK = '#f1f5f9'

function compactWon(value) {
  const n = Math.round(Number(value) || 0)
  if (n >= 100_000_000) return `${(n / 100_000_000).toFixed(1).replace(/\.0$/, '')}억`
  if (n >= 10_000) return `${Math.round(n / 10_000).toLocaleString('ko-KR')}만`
  return n.toLocaleString('ko-KR')
}

export default function HiddenInsuranceClaimComparisonChart({
  estimatedClaimable,
  reimbursementTotal,
  claimRatio,
  unclaimedHint,
  compact = false,
  slim = false,
}) {
  const estimated = Math.max(0, Number(estimatedClaimable) || 0)
  const received = Math.max(0, Number(reimbursementTotal) || 0)
  const unclaimed = Math.max(0, Number(unclaimedHint) || 0)
  const maxVal = Math.max(estimated, received, 1)

  const isSlim = compact && slim
  const labelW = isSlim ? 64 : compact ? 72 : 108
  const valueW = isSlim ? 52 : compact ? 56 : 72
  const chartX = labelW + 6
  const chartW = 640 - chartX - valueW - 6
  const barH = isSlim ? 16 : compact ? 22 : 30
  const row1Y = isSlim ? 18 : compact ? 28 : 36
  const row2Y = isSlim ? 44 : compact ? 62 : 82
  const svgHeight = isSlim ? 88 : compact ? 118 : 168
  const footY = isSlim ? 78 : compact ? 108 : 156

  const estBarW = Math.max(estimated > 0 ? 4 : 0, (estimated / maxVal) * chartW)
  const recBarW = Math.max(received > 0 ? 4 : 0, (received / maxVal) * chartW)
  const gapStartX = chartX + recBarW
  const gapW = Math.max(0, estBarW - recBarW)

  const ratioLabel = claimRatio != null && !Number.isNaN(Number(claimRatio))
    ? `${Number(claimRatio)}%`
    : '-'

  const labelSize = isSlim ? 9 : compact ? 10 : 11
  const footSize = isSlim ? 8 : compact ? 9 : 10
  const textDy = isSlim ? 12 : compact ? 15 : 20
  const ratioY = isSlim ? 12 : compact ? 14 : 16
  const showFootnote = !isSlim

  if (estimated <= 0 && received <= 0) {
    return (
      <div className="hi-report-chart rounded-lg border border-slate-100 bg-slate-50 px-3 py-3 text-center text-xs text-slate-500">
        비교할 금액 데이터가 없습니다.
      </div>
    )
  }

  return (
    <div className={`hi-report-chart ${isSlim ? 'mt-1' : ''}`}>
      <svg
        viewBox={`0 0 640 ${svgHeight}`}
        className={`h-auto w-full ${isSlim ? 'max-h-[88px]' : ''}`}
        role="img"
        aria-label={`추정 청구 ${formatWon(estimated)}, 실손 수령 ${formatWon(received)}`}
      >
        {!compact ? (
          <text x="0" y="16" fill="#64748b" fontSize="11" fontWeight="600">
            추정 청구 vs 실손 수령
          </text>
        ) : null}
        <text x={640} y={ratioY} fill="#1e3a5f" fontSize={labelSize} fontWeight="700" textAnchor="end">
          수령 비율 {ratioLabel}
        </text>

        <text x="0" y={row1Y + textDy} fill="#475569" fontSize={labelSize} fontWeight="500">
          추정 청구
        </text>
        <rect x={chartX} y={row1Y} width={chartW} height={barH} rx="4" fill={COLOR_TRACK} />
        <rect x={chartX} y={row1Y} width={estBarW} height={barH} rx="4" fill={COLOR_ESTIMATED} stroke={COLOR_ESTIMATED_STROKE} strokeWidth="1" />
        <text x={640} y={row1Y + textDy} fill="#0f172a" fontSize={labelSize} fontWeight="700" textAnchor="end">
          {compactWon(estimated)}원
        </text>

        <text x="0" y={row2Y + textDy} fill="#475569" fontSize={labelSize} fontWeight="500">
          실손 수령
        </text>
        <rect x={chartX} y={row2Y} width={chartW} height={barH} rx="4" fill={COLOR_TRACK} />
        <rect x={chartX} y={row2Y} width={recBarW} height={barH} rx="4" fill={COLOR_RECEIVED} />
        {gapW > 6 && estimated > received ? (
          <rect
            x={gapStartX}
            y={row2Y - 2}
            width={gapW}
            height={barH + 4}
            rx="3"
            fill={COLOR_GAP}
            stroke={COLOR_GAP_STROKE}
            strokeWidth="1"
            strokeDasharray="4 3"
            opacity="0.95"
          />
        ) : null}
        <text x={640} y={row2Y + textDy} fill="#0f172a" fontSize={labelSize} fontWeight="700" textAnchor="end">
          {compactWon(received)}원
        </text>

        {showFootnote ? (
          unclaimed > 0 ? (
            <text x={chartX} y={footY} fill="#b45309" fontSize={footSize} fontWeight="600">
              {compact ? `미수령 가능성 ${formatWon(unclaimed)}` : `추정 대비 미수령 가능성 약 ${formatWon(unclaimed)}`}
            </text>
          ) : (
            <text x={chartX} y={footY} fill="#64748b" fontSize={footSize}>
              {compact ? '수령 비율 양호' : '추정 청구 가능액 대비 실손 수령이 비슷하거나 높게 집계되었습니다.'}
            </text>
          )
        ) : null}
      </svg>

      {isSlim ? (
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-[9px] text-slate-500">
          <span>■ 추정 청구</span>
          <span>■ 실손 수령</span>
          {unclaimed > 0 ? <span>▨ 미청구 여지</span> : null}
          {unclaimed > 0 ? <span className="text-amber-700">미수령 {formatWon(unclaimed)}</span> : null}
        </div>
      ) : null}

      {!compact ? (
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_ESTIMATED, border: `1px solid ${COLOR_ESTIMATED_STROKE}` }} />
            추정 청구 가능액
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_RECEIVED }} />
            실제 실손 수령
          </span>
          {unclaimed > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: COLOR_GAP, border: `1px dashed ${COLOR_GAP_STROKE}` }}
              />
              미청구 여지
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
